import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SiteShell from "@/components/SiteShell";
import SEO from "@/components/SEO";
import NotFound from "@/pages/NotFound";
import api from "@/lib/api";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "https://saans-pcd-franchise.emergent.host";

export default function BlogPost() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, post: null, related: [] });

  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then(({ data }) => setState({ loading: false, post: data.post, related: data.related || [] }))
      .catch(() => setState({ loading: false, post: null, related: [] }));
  }, [slug]);

  if (state.loading) return <SiteShell><div className="p-32 text-center text-[#6b6a65]">Loading…</div></SiteShell>;
  if (!state.post) return <NotFound />;

  const { post, related } = state;
  const jsonLd = [{
    "@context": "https://schema.org", "@type": "Article",
    headline: post.title, image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.created_at, author: { "@type": "Organization", name: "Saans Wellness" },
    publisher: { "@type": "Organization", name: "Saans Wellness", logo: { "@type": "ImageObject", url: ORIGIN + "/favicon.svg" } },
    mainEntityOfPage: `${ORIGIN}/blog/${post.slug}`,
    description: post.seo_description || post.excerpt,
  }];

  return (
    <SiteShell>
      <SEO title={`${post.seo_title || post.title} | Saans Wellness`} description={post.seo_description || post.excerpt}
        path={`/blog/${post.slug}`} image={post.cover_image} type="article" jsonLd={jsonLd} />

      <article className="bg-[#f9f8f5] pt-32 pb-16 md:pt-40" data-testid="blog-post-page">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Link to="/blog" className="text-sm font-medium text-[#6b6a65] hover:text-[#d46a1d]">← All posts</Link>
          {post.tags && <div className="mt-6 text-xs font-semibold uppercase tracking-wide text-[#1f5d3a]">{post.tags}</div>}
          <h1 className="mt-3 font-serif text-4xl leading-tight tracking-tight text-[#202124] md:text-5xl">{post.title}</h1>
          {post.created_at && <div className="mt-3 text-sm text-[#8a8880]">{new Date(post.created_at).toLocaleDateString()}</div>}
          {post.cover_image && (
            <div className="mt-8 overflow-hidden rounded-2xl">
              <img src={post.cover_image} alt={post.title} className="w-full object-cover" decoding="async" />
            </div>
          )}
          <div className="mt-8 space-y-4 text-[17px] leading-relaxed text-[#3a3a37]">
            {post.content.split("\n").filter((l) => l.trim()).map((line, i) => <p key={i}>{line}</p>)}
          </div>

          <div className="mt-12 rounded-2xl bg-[#1f5d3a] p-8 text-center text-white">
            <h2 className="font-serif text-2xl">Ready to start your franchise?</h2>
            <p className="mt-2 text-[#d6e6dc]">Apply now and our team will call you within 24 hours.</p>
            <Link to="/#franchise" className="mt-5 inline-flex rounded-full bg-[#d46a1d] px-6 py-3 text-sm font-semibold text-white hover:bg-[#c05f16]">Apply for Franchise</Link>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mx-auto mt-16 max-w-7xl px-5 md:px-8">
            <h2 className="font-serif text-2xl text-[#202124]">Related posts</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="rounded-2xl border border-[#e7e0d6] bg-white p-5 card-shadow hover:border-[#d46a1d]">
                  <h3 className="font-serif text-lg text-[#202124]">{r.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-[#6b6a65]">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </SiteShell>
  );
}
